import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {  ArrowLeft, Loader2, Sparkles, Crown, Zap, ShoppingBag } from 'lucide-react';
import { GemIcon } from '../components/GemIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GemShop() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [userGems, setUserGems] = useState(0);

  useEffect(() => {
    fetchPackages();
    fetchGems();
  }, []);

  const fetchPackages = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/gems/packages`);
      setPackages(data);
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGems = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/user/gems`, { withCredentials: true });
      setUserGems(data.gems || 0);
    } catch (err) {
      console.error('Failed to fetch gems:', err);
    }
  };

  const handlePurchase = async (pkg) => {
    setPurchasing(pkg.id);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/gems/purchase`,
        { package_id: pkg.id, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(null);
    }
  };

  const PACKAGE_STYLES = [
    { gradient: 'from-blue-500/20 to-cyan-500/10', border: 'border-blue-500/30', accent: 'text-blue-400', bg: 'bg-blue-500' },
    { gradient: 'from-purple-500/20 to-pink-500/10', border: 'border-purple-500/30', accent: 'text-purple-400', bg: 'bg-purple-500', popular: true },
    { gradient: 'from-[#F59E0B]/20 to-[#D97706]/10', border: 'border-[#F59E0B]/30', accent: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]' },
    { gradient: 'from-[#E11D48]/20 to-[#F59E0B]/10', border: 'border-[#E11D48]/30', accent: 'text-[#E11D48]', bg: 'bg-[#E11D48]', best: true },
  ];

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-12 z-40 bg-[#050505] border-b border-white/5 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-[#A1A1AA] hover:text-white" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white font-['Unbounded']">Gem Shop</h1>
            <p className="text-xs text-[#71717A]">Power up your experience</p>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-full">
            <GemIcon size={16} />
            <span className="text-sm font-bold text-[#F59E0B]" data-testid="current-gems">{userGems.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* What are Gems */}
      <div className="px-4 pt-6 pb-4">
        <div className="p-4 bg-gradient-to-br from-[#F59E0B]/10 to-[#E11D48]/5 border border-[#F59E0B]/20 rounded-2xl">
          <h2 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
            <GemIcon size={18} /> What can you do with Gems?
          </h2>
          <div className="space-y-2 text-xs text-[#A1A1AA]">
            <div className="flex items-center gap-2">
              <Crown className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0" />
              <span><strong className="text-white">3,000 gems</strong> = 100 Premium messages</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-[#E11D48] flex-shrink-0" />
              <span><strong className="text-white">6,000 gems</strong> = 100 Ultimate messages</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
              <span><strong className="text-white">1,500 gems</strong> = Boost a character for 2h visibility</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gem Packages */}
      <div className="px-4">
        <h3 className="text-xs font-medium text-[#71717A] uppercase tracking-wider mb-3">Choose a Package</h3>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#E11D48] animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {packages.map((pkg, i) => {
              const style = PACKAGE_STYLES[i] || PACKAGE_STYLES[0];
              return (
                <button
                  key={pkg.id}
                  onClick={() => handlePurchase(pkg)}
                  disabled={purchasing === pkg.id}
                  className={`w-full p-4 rounded-xl border bg-gradient-to-br ${style.gradient} ${style.border} flex items-center gap-4 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 relative overflow-hidden`}
                  data-testid={`gem-package-${pkg.id}`}
                >
                  {style.popular && (
                    <div className="absolute top-0 right-0 px-2.5 py-0.5 bg-purple-500 text-[10px] font-bold text-white rounded-bl-lg">
                      POPULAR
                    </div>
                  )}
                  {style.best && (
                    <div className="absolute top-0 right-0 px-2.5 py-0.5 bg-[#E11D48] text-[10px] font-bold text-white rounded-bl-lg">
                      BEST VALUE
                    </div>
                  )}
                  <div className={`w-14 h-14 ${style.bg} rounded-xl flex items-center justify-center shadow-lg`}>
                    <GemIcon size={36} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-white font-bold text-base">{pkg.label}</p>
                    <p className="text-xs text-[#71717A]">{pkg.gems.toLocaleString()} gems</p>
                  </div>
                  <div className="text-right">
                    {purchasing === pkg.id ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <span className="text-white font-bold text-lg">
                        {pkg.currency === 'gbp' ? '£' : '$'}{pkg.amount.toFixed(2)}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
